<script lang="ts">
export default {
  name: 'BinderUploadPanel',
};
</script>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { FileUp, Loader, AlertTriangle } from '@lucide/vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import { externalPolicyBinderService } from '@/services/v2/ExternalPolicyBinderService';
import { aiService } from '@/services/AIService';
import { ExternalPolicyBinderSubmission, MAX_BINDER_SIZE_BYTES } from '@/types/ExternalPolicyBinder';
import { AIPrompt } from '@/types/ai';
import { useToast } from '@/composables/useToast';

const props = defineProps<{
  accountId: string;
}>();

const emit = defineEmits<{
  submitted: [submission: ExternalPolicyBinderSubmission];
}>();

const toast = useToast();
const draggingOver = ref(false);
// Which prompt reads the binder decides which fields come out of it, so the broker picks
// rather than inheriting whichever external prompt happened to be published last.
const prompts = ref<AIPrompt[]>([]);
const promptVersionId = ref<number | null>(null);
const loadingPrompts = ref(true);

const promptOptions = computed(() =>
  prompts.value.map((prompt) => ({
    label: prompt.version === 'DRAFT' ? prompt.name : `${prompt.name} (v${prompt.version})`,
    value: String(prompt.promptVersionId),
  })),
);

const hasPrompts = computed(() => prompts.value.length > 0);
const isSubmitting = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const duplicateSubmissionId = ref<number | null>(null);

function validateFile(file: File): boolean {
  if (!file.name.toLowerCase().endsWith('.pdf')) {
    toast.error('Only PDF files are supported for binder scanning.');
    return false;
  }
  if (file.size > MAX_BINDER_SIZE_BYTES) {
    toast.error('Binder files must be 25 MB or smaller.');
    return false;
  }
  return true;
}

async function submitFile(file: File) {
  if (!validateFile(file) || isSubmitting.value) {
    return;
  }
  if (!promptVersionId.value) {
    toast.error('Choose an extraction prompt before uploading a binder.');
    return;
  }

  isSubmitting.value = true;
  duplicateSubmissionId.value = null;
  try {
    const submission = await externalPolicyBinderService.submitBinder(props.accountId, file, promptVersionId.value);
    emit('submitted', submission);
  } catch (err) {
    const cause = (err as { cause?: { status?: number; data?: { existingSubmissionId?: number } } })?.cause;
    if (cause?.status === 409) {
      duplicateSubmissionId.value = cause.data?.existingSubmissionId ?? null;
      toast.error('This binder was already submitted.');
    } else {
      toast.error('Binder upload failed. Please try again.');
    }
  } finally {
    isSubmitting.value = false;
  }
}

function triggerFilePicker() {
  if (!promptVersionId.value) {
    return;
  }
  fileInput.value?.click();
}

async function handleFileChosen(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (file) {
    await submitFile(file);
  }
}

function handleDragOver(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = true;
}

function handleDragLeave(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = false;
}

async function handleDrop(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = false;

  let file: File | null = null;
  if (e.dataTransfer?.items) {
    for (const item of e.dataTransfer.items) {
      if (item.kind === 'file') {
        file = item.getAsFile();
        if (file) break;
      }
    }
  }
  if (!file && e.dataTransfer?.files?.length) {
    file = e.dataTransfer.files[0];
  }
  if (file) {
    await submitFile(file);
  }
}

// Only published external prompts can extract a binder; a draft has no model settled yet.
onMounted(async () => {
  try {
    const all = await aiService.getAllPrompts('external');
    prompts.value = all.filter((prompt) => !prompt.isDraft);
    if (prompts.value.length === 1) {
      promptVersionId.value = prompts.value[0].promptVersionId;
    }
  } catch {
    prompts.value = [];
  } finally {
    loadingPrompts.value = false;
  }
});

function goToDuplicate() {
  if (duplicateSubmissionId.value) {
    emit('submitted', {
      submissionId: duplicateSubmissionId.value,
      accountId: Number(props.accountId),
    } as ExternalPolicyBinderSubmission);
  }
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm text-base-content/60 italic">
      Upload a binder PDF and we'll extract the policy details automatically. You'll review everything before a policy
      is created.
    </p>

    <div
      v-if="loadingPrompts"
      class="flex items-center gap-2 text-sm"
      data-testid="binder-prompts-loading">
      <Loader :size="16" class="animate-spin text-secondary" />
      <span>Loading extraction prompts…</span>
    </div>

    <!-- Nothing can read a binder until someone has published an external prompt, so say
         that rather than offering an upload that would be rejected. -->
    <div
      v-else-if="!hasPrompts"
      class="alert alert-warning"
      data-testid="binder-no-prompts">
      <AlertTriangle :size="18" />
      <span>
        No published external extraction prompt exists yet. One has to be created and published in the AI prompt
        manager before a binder can be scanned.
      </span>
    </div>

    <template v-else>
      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium">Extraction prompt</span>
        <AppSelect
          :model-value="promptVersionId ? String(promptVersionId) : ''"
          :options="promptOptions"
          data-testid="binder-prompt-select"
          @update:model-value="(value: string) => (promptVersionId = value ? Number(value) : null)" />
        <span class="text-xs text-base-content/60">Decides which fields are pulled from the binder.</span>
      </label>

    <div
      class="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-10 transition-colors"
      :class="[
        draggingOver ? 'border-secondary bg-secondary/5' : 'border-base-300',
        promptVersionId ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
      ]"
      data-testid="binder-drop-zone"
      @click="triggerFilePicker"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop">
      <Loader v-if="isSubmitting" :size="32" class="animate-spin text-secondary" />
      <FileUp v-else :size="32" class="text-secondary" />
      <div class="text-center">
        <p v-if="isSubmitting" class="font-medium">Uploading binder…</p>
        <template v-else-if="!promptVersionId">
          <p class="font-medium">Choose an extraction prompt first</p>
          <p class="text-sm text-base-content/60">Then drop a binder PDF here</p>
        </template>
        <template v-else>
          <p class="font-medium">Drop a binder PDF here or click to browse</p>
          <p class="text-sm text-base-content/60">PDF only, up to 25 MB</p>
        </template>
      </div>
      <input
        ref="fileInput"
        type="file"
        class="hidden"
        accept="application/pdf,.pdf"
        data-testid="binder-file-input"
        @click.stop
        @change="handleFileChosen" />
    </div>
    </template>

    <div v-if="duplicateSubmissionId" class="alert alert-warning" data-testid="binder-duplicate-warning">
      <span>
        This binder was already submitted.
        <button class="link" @click="goToDuplicate">View the existing submission</button>
      </span>
    </div>
  </div>
</template>
